import { zodResolver } from '@hookform/resolvers/zod';
import { Icon } from '@iconify/react';
import { useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import i18n from '@/Config/app/i18n';
import { ParagraphDto } from '@/Domain/api/KnowledgeBase/dto/Article.dto';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Form } from '@/Domain/components/Shared/Form/Form';
import { FormItem, FormMessage } from '@/Domain/components/Shared/FormCustom/FormCustom';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { ArticleTypeEnum } from '@/Domain/enum/ArticleType.enum';
import { useAutoSubmit } from '@/Domain/hooks/App/useAutoSave.hook';
import { useRemoveParagraph } from '@/Domain/hooks/KnowledgeBase/useRemoveParagraph/useRemoveParagraph.hook';
import { useUpdateParagraph } from '@/Domain/hooks/KnowledgeBase/useUpdateParagraph.hook';

type Props = {
  paragraph: ParagraphDto;
  index: number;
  shouldFocus?: boolean;
};

const ParagraphValidator = z.object({
  id: z.number(),
  type: z.literal(ArticleTypeEnum.Paragraph),
  content: z
    .string()
    .min(1, i18n.t('paragraphs:validation.snippet_min'))
    .max(500, i18n.t('paragraphs:validation.snippet_max')),
});

type ParagraphForm = z.infer<typeof ParagraphValidator>;

export const ParagraphItemForm = ({ paragraph, index, shouldFocus = false }: Props) => {
  const { t } = useTranslation('paragraphs');
  const inputRef = useRef<HTMLInputElement>(null);

  const { handleRemoveParagraph, isPending } = useRemoveParagraph();
  const { handleUpdateParagraph } = useUpdateParagraph(paragraph.id);

  const form = useForm<ParagraphDto>({
    mode: 'onChange',
    defaultValues: {
      id: paragraph.id,
      type: ArticleTypeEnum.Paragraph,
      content: paragraph.content || '',
    },
    resolver: zodResolver(ParagraphValidator),
  });

  useAutoSubmit(form, (data) => handleUpdateParagraph(data as ParagraphForm));

  useEffect(() => {
    if (shouldFocus && inputRef.current) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [shouldFocus]);

  return (
    <Form {...form}>
      <form
        data-testid="paragraph-form"
        onSubmit={form.handleSubmit((data) => {
          handleUpdateParagraph(data);
        })}
      >
        <section className="grid grid-cols-[0.15fr_1fr_0.01fr]  gap-x-4 px-4 py-5 items-center">
          <p className="text-foreground-subtle text-sm whitespace-nowrap ">
            {t('modal.list.item', { count: index + 1 })}
          </p>
          <Controller
            name="content"
            render={({ field, fieldState }) => (
              <FormItem>
                <Input
                  ref={inputRef}
                  placeholder={t('create.form.content_placeholder')}
                  size="sm"
                  className="m-1"
                  error={!!fieldState.error}
                  disabled={isPending}
                  name={field.name}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                />

                <FormMessage error={fieldState.error} />
              </FormItem>
            )}
          />
          <div className="w-min text-right flex gap-2 justify-self-end self-start mt-1">
            <Tooltip>
              <TooltipTrigger asChild tabIndex={-1}>
                <Button
                  data-testid="remove-button"
                  variant="secondary"
                  size="icon"
                  className="hover:!text-red-500"
                  onClick={() => handleRemoveParagraph(paragraph.id)}
                  disabled={isPending}
                >
                  <Icon icon="mingcute:delete-line" />
                </Button>
              </TooltipTrigger>
              <TooltipContent align="end">{t('tooltip.delete')}</TooltipContent>
            </Tooltip>
          </div>
        </section>
      </form>
      <Separator />
    </Form>
  );
};
